import type { Article } from "../articles";

export const articleHowToPhotographRoofForInsuranceClaim: Article = {
  slug: "how-to-photograph-roof-for-insurance-claim",
  title: "How to Photograph a Roof for Insurance Claim (Step-by-Step)",
  description: "Proper documentation can mean the difference between a full payout and a denied claim. Get our step-by-step checklist, angles that matter, and photos to take.",
  category: "Insurance",
  tags: ["Insurance Claims", "Roof Damage", "Documentation", "Storm Damage", "Home Insurance"],
  createdAt: "June 24, 2026",
  updatedAt: "June 29, 2026",
  lastVerified: "June 29, 2026",
  readTime: "8 min read",
  keyTakeaway: `Take photos from 5 angles: ground-level (all 4 sides), close-ups of damage (with measurement reference), attic interior (light coming through), gutters (granules), and wide shots showing damage context. Date-stamp everything. Document before making any repairs.`,
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "Insurance Information Institute (III) - Documenting Property Damage",
      url: "https://www.iii.org"
    },
    {
      name: "National Association of Public Insurance Adjusters (NAPIA)",
      url: "https://www.napia.com"
    }
  ],
  keyMetrics: [
    { label: "Photos to Take", value: "50-100+ images" },
    { label: "Documentation Time", value: "30-45 minutes" },
    { label: "Claim Decision", value: "15-30 days after filing" },
    { label: "Evidence Quality", value: "Critical for approval" }
  ],
  faqs: [
    {
      question: "Should I get on the roof to take photos?",
      answer: "NO. It's dangerous and unnecessary. Use a zoom lens or drone from the ground. If you must get closer, use a sturdy ladder with a spotter, but never walk on a damaged roof. Insurance doesn't pay for injuries sustained during documentation."
    },
    {
      question: "How soon after damage should I take photos?",
      answer: "Immediately, within 24 hours if safe. Weather can worsen damage, and you want to document the initial state. If a storm is ongoing, wait until it passes but photograph before making any temporary repairs."
    },
    {
      question: "Do I need professional photos or is my phone okay?",
      answer: "Modern smartphones (iPhone 12+, recent Androids) have excellent cameras. What matters is technique, not equipment. Use good lighting, steady hands, and include reference objects. Professional photos add credibility but aren't required."
    }
  ],
  content: `
    <p>
      The adjuster is coming in three days. Your roof has visible damage from the storm, missing shingles, dented flashing, granules everywhere. You took a few photos from the driveway with your phone, figuring that's enough.
    </p>
    <p>
      Two weeks later, the claim decision arrives: "Insufficient evidence of storm damage. Claim denied."
    </p>
    <p>
      What went wrong? Your photos showed a roof. They didn't show <em>damage</em>, not in a way that proves storm impact versus normal wear and tear. Adjusters see thousands of roof photos. They need specific angles, close-ups with scale references, and context that tells a clear story.
    </p>
    <p>
      This guide shows you how to document roof damage like a public adjuster would, because the quality of your evidence directly impacts your payout (to understand how policies and coverages evaluate your claim, read our guide on <a href="/articles/does-homeowners-insurance-cover-roof-replacement/" class="text-[var(--accent)] hover:underline font-semibold">does homeowners insurance cover roof replacement?</a>).
    </p>

    <h2>Before You Start: Safety and Timing</h2>
    <p>
      <strong>Safety first:</strong> Never walk on a damaged roof. Use a ladder from the ground with a spotter, or photograph from ground level with a zoom lens. If you have a drone, use it, but stay at least 50 feet from power lines.
    </p>
    <p>
      <strong>Timing matters:</strong> Photograph damage as soon as it's safe to do so (within 24-48 hours). Natural light is best, shoot between 10 AM and 3 PM when shadows are minimal. Avoid shooting in rain, fog, or low light.
    </p>
    <p>
      <strong>Before any repairs:</strong> Document EVERYTHING before making temporary repairs or allowing contractors to touch the roof. Once repairs begin, evidence is destroyed.
    </p>

    <h2>The 5 Essential Photo Angles</h2>

    <h3>1. Ground-Level Overview (All 4 Sides)</h3>
    <p>
      Stand 20-30 feet from your house and photograph the entire roof from each side:
    </p>
    <ul>
      <li><strong>Front elevation:</strong> Shows overall roof condition and any visible missing shingles.</li>
      <li><strong>Rear elevation:</strong> Often where storm damage is worst (wind comes from one direction).</li>
      <li><strong>Left and right sides:</strong> Capture roof valleys, flashing, and any sagging.</li>
    </ul>
    <p>
      These photos establish context. They show the adjuster your roof's overall condition before the storm.
    </p>

    <h3>2. Close-Ups of Specific Damage</h3>
    <p>
      Get within 2-3 feet of each damaged area:
    </p>
    <ul>
      <li><strong>Missing shingles:</strong> Photograph the exposed underlayment and nail pattern.</li>
      <li><strong>Cracked or bruised shingles:</strong> Show the fracture lines or impact marks.</li>
      <li><strong>Lifted or curled shingles:</strong> Show where wind has broken the sealant strip.</li>
      <li><strong>Dented or torn flashing:</strong> Around chimneys, vents, and valleys.</li>
      <li><strong>Granule loss:</strong> Bald spots where asphalt is exposed (check out our guide on the <a href="/articles/10-signs-you-need-a-new-roof/" class="text-[var(--accent)] hover:underline font-semibold">10 signs you need a new roof</a> for more indicators).</li>
    </ul>
    <p>
      <strong>Include a size reference:</strong> Place a coin, ruler, or tape measure next to the damage. This proves the damage is real and gives scale. A dime next to a hail dent shows the dent's size better than a photo alone.
    </p>

    <!--COMPONENT:info-note|label="Pro Tip"-->
    Take 3 photos of each damaged area: wide shot (shows location on roof), medium shot (shows damage in context), and close-up (shows detail with size reference). This "rule of three" gives adjusters complete information.
    <!--ENDOFCOMPONENT-->

    <h3>3. Attic Interior Photos</h3>
    <p>
      If your roof is leaking, photograph from inside the attic:
    </p>
    <ul>
      <li><strong>Light penetration:</strong> Turn off attic lights during daytime. Photograph any spots where daylight shines through: this proves holes exist.</li>
      <li><strong>Water stains:</strong> Show discoloration on rafters, sheathing, or insulation.</li>
      <li><strong>Active drips:</strong> If water is actively dripping, capture it. Use a flashlight to illuminate droplets.</li>
      <li><strong>Wet insulation:</strong> Matted or discolored insulation indicates ongoing leaks.</li>
    </ul>
    <p>
      Attic photos prove damage isn't just cosmetic: it's affecting your home's interior.
    </p>

    <h3>4. Gutter and Downspout Photos</h3>
    <p>
      Granules in gutters prove shingle damage:
    </p>
    <ul>
      <li><strong>Fill your gutters:</strong> Photograph granule accumulation. Run your finger through granules and photograph your hand covered in them.</li>
      <li><strong>Downspout discharge:</strong> If granules are flowing out of downspouts during rain, photograph this. It proves active deterioration.</li>
      <li><strong>Bucket test:</strong> Place a white bucket under a downspout during rain. Photograph the granule-laden water. This is compelling evidence.</li>
    </ul>

    <h3>5. Context and Comparison Photos</h3>
    <p>
      Show damage relative to undamaged areas:
    </p>
    <ul>
      <li><strong>Before/after:</strong> If you have pre-storm photos (from Google Street View, real estate listings, or previous inspections), include them.</li>
      <li><strong>Damaged vs. intact:</strong> Photograph a damaged section next to an undamaged section. This proves the damage is storm-related, not normal aging.</li>
      <li><strong>Neighborhood context:</strong> If neighbors have similar damage, photograph their roofs (from public streets). This proves a storm event affected the entire area.</li>
    </ul>

    <h2>Technical Photo Tips</h2>
    <ul>
      <li><strong>Enable date/time stamps:</strong> Most smartphones embed this in metadata. Verify it's accurate. Adjusters need to know photos were taken immediately after the storm.</li>
      <li><strong>Use high resolution:</strong> Set your camera to maximum resolution. Adjusters may zoom in to examine details.</li>
      <li><strong>Steady your hands:</strong> Blurry photos are useless. Use two hands, brace against something solid, or use a tripod.</li>
      <li><strong>Avoid flash:</strong> Flash creates glare on shingles. Use natural light or external lighting positioned at an angle.</li>
      <li><strong>Clean your lens:</strong> Wipe your phone camera with a soft cloth. Smudged lenses create hazy photos.</li>
      <li><strong>Take too many photos:</strong> Better to have 100 photos and not need them than to need 10 and only have 5.</li>
    </ul>

    <h2>Common Photo Mistakes That Get Claims Denied</h2>
    <ul>
      <li><strong>Only photographing from the street:</strong> Distant photos don't show damage detail. Get closer (safely).</li>
      <li><strong>No size reference:</strong> A crack in a shingle could be 1 inch or 6 inches. Without a reference, adjusters assume the worst (smallest).</li>
      <li><strong>Poor lighting:</strong> Dark, shadowy photos hide damage. Shoot in bright, even light.</li>
      <li><strong>Only photographing the worst damage:</strong> Adjusters suspect cherry-picking. Photograph the entire roof, not just one bad spot.</li>
      <li><strong>Waiting too long:</strong> Photos taken 2 weeks after the storm raise questions. Were you diligent about documentation?</li>
      <li><strong>Editing or filtering:</strong> Never use filters, brightness adjustments, or editing apps. Adjusters can detect manipulation and will discount your evidence.</li>
    </ul>

    <h2>Organizing and Submitting Your Photos</h2>
    <p>
      Don't just email 100 random photos. Organize them:
    </p>
    <ul>
      <li><strong>Create folders:</strong> "Exterior-Front," "Exterior-Rear," "CloseUps-MissingShingles," "Attic-Interior," "Gutters."</li>
      <li><strong>Name files descriptively:</strong> "Front-Roof-MissingShingles-NorthSide.jpg" not "IMG_4521.jpg."</li>
      <li><strong>Write a summary:</strong> Include a one-page document listing all damage areas with photo references. Example: "Photo A1-A5: Front elevation showing 12 missing shingles on north-facing slope."</li>
      <li><strong>Submit promptly:</strong> File your claim within 24-48 hours and submit photos immediately. Don't wait for the adjuster to request them.</li>
    </ul>

    <h2>Should You Hire a Professional?</h2>
    <p>
      For minor damage (a few shingles), your own photos are sufficient. For major damage (100+ sq ft, structural concerns, or claims over $10,000), consider:
    </p>
    <ul>
      <li><strong>Drone photography:</strong> Professionals have FAA-certified drones that capture roof-wide imagery safely. Cost: $200-$500.</li>
      <li><strong>Public adjuster:</strong> They document damage professionally and advocate for you during the claims process. They take 10-20% of the settlement but often maximize payouts enough to cover their fee.</li>
      <li><strong>Roofing contractor documentation:</strong> Reputable contractors provide detailed photo documentation with estimates. This strengthens your claim.</li>
    </ul>

    <h2>Summary: Your Photos Are Your Best Evidence</h2>
    <p>
      Your photos are your evidence. They tell the story of what happened to your roof. Vague, poorly lit, or incomplete photos create doubt. Clear, comprehensive, well-organized photos create certainty.
    </p>
    <p>
      Adjusters approve claims when the evidence is undeniable. Make your evidence undeniable.
    </p>
  `
};